/**
 * Reads and writes window.localStorage directly, instead of using
 * common/util/localStorage's LocalStorage wrapper.
 *
 * That wrapper cannot be imported first from a fresh module: it sits in the
 * cycle localStorage -> util-helpers -> util-consts -> localStorage, and
 * util-consts.jsx:588 calls LocalStorage.get("lookup") at module-eval time, so
 * the cycle hands it an uninitialised module and it throws. Using window.localStorage
 * directly instead makes this module order-independent.
 */

const STORAGE_KEY_PREFIX = 'notifications_state_';

function getContactId() {
    try {
        const raw = window.localStorage.getItem('user');
        const user = raw === null ? null : JSON.parse(raw);
        return user?.Contact?.ContactID ?? null;
    } catch (error) {
        return null;
    }
}

function getStorageKey(contactId) {
    return `${STORAGE_KEY_PREFIX}${contactId}`;
}

// Stores the notifications themselves (not just read/cleared markers), unlike the
// pre-Phase-2 version of this module: that version relied on a full-list "OnNotifications"
// socket push on every connect and only needed to persist markers to reapply on top of
// it. Phase 2 removed that socket event entirely (the REST API + this fallback are now
// the only two ways the bell's list survives a refresh), so this module must be the
// list's source of truth when backend persistence is flagged off.
export function loadPersistedNotifications() {
    const contactId = getContactId();
    if (!contactId) {
        return {list: [], count: 0};
    }

    try {
        const raw = window.localStorage.getItem(getStorageKey(contactId));
        const list = raw === null ? [] : JSON.parse(raw);
        const safeList = Array.isArray(list) ? list : [];
        return {list: safeList, count: safeList.length};
    } catch (error) {
        return {list: [], count: 0};
    }
}

export function savePersistedNotifications(list) {
    const contactId = getContactId();
    if (!contactId) {
        return;
    }

    try {
        window.localStorage.setItem(getStorageKey(contactId), JSON.stringify(list ?? []));
    } catch (error) {
        // Storage quota exceeded or unavailable - silently fail, same as before.
    }
}

// Called on logout so the previous user's cached notification content doesn't
// remain readable in localStorage for the next person to use this browser
// (e.g. a shared/kiosk machine).
export function clearPersistedNotifications(contactId) {
    if (!contactId) {
        return;
    }

    try {
        window.localStorage.removeItem(getStorageKey(contactId));
    } catch (error) {
        // Storage unavailable - nothing to clean up.
    }
}
